import * as React from 'react';
import {
	CheckoutOrderFragment,
	InfoMessageTypeEnum,
	NASProductCategory,
	NASProductFragment,
	NASSelectedProductFragment,
	NASStatus
} from '@websky/graphql';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme';
import { addMoney } from '@shared/lib/money';
import { Money } from '../types';
import NasServiceCard from './NasServices/NasServiceCard';
import { Tooltip } from '../BaseComponents';
import { FormControlLabel } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import InfoLinksLabel from '../InfoLinksLabel';
import Button from '../BaseComponents/Button';
import { useAirportServicesRefund } from './hooks';
import { getTravellerName } from '../utils';
import Notification from '../Notification';

interface Props {
	services: NASProductFragment[];
	selectedServices: NASSelectedProductFragment[];
	onSuccessfullyRefunded: () => void;
	onCancel?: () => void;
	order: CheckoutOrderFragment;
	travellersToRefund: string[];
}

const NasRefundServices: React.FC<Props> = props => {
	const { t } = useTranslation('Refund');
	const css = useTheme('NasRefundServices');

	const travellers = props.order.travellers;

	const [activeCheckbox, setActiveCheckbox] = React.useState(false),
		[showAgreeTooltip, setShowAgreeTooltip] = React.useState(false);

	React.useEffect(() => {
		if (activeCheckbox) {
			setShowAgreeTooltip(false);
		}
	}, [activeCheckbox]);

	const { refund, loading, refundedWithError, refundedServiceIds } = useAirportServicesRefund(props.order.id);

	const servicesToRefund = React.useMemo(() => {
		const servicesToRefund: {
			serviceId: string;
			type: NASProductCategory;
			totalPrice: Money;
			ticketsNumber: number;
			serviceName: {
				name: string;
				terminal: string;
			};
			passengerNames: string[];
			passengerIds: string[];
		}[] = [];

		props.selectedServices.forEach(selected => {
			const service = props.services.find(service => service.id === selected.product.id);

			let priceToRefund: Money = {
				amount: 0,
				currency: null
			};

			const passengerNames: string[] = [];
			const passengerIds: string[] = [];

			let ticketsNumber = 0;

			selected.orders.forEach(order => {
				if (
					props.travellersToRefund.includes(order.travellers.id) &&
					order.status === NASStatus.Confirmed &&
					order.isRefundable
				) {
					const passengerPrice = selected.fare.prices.find(
						price => price.traveller.id === order.travellers.id
					);

					priceToRefund = addMoney(priceToRefund, passengerPrice.price);

					const traveller = travellers.find(traveller => traveller.id === order.travellers.id);

					passengerNames.push(getTravellerName(traveller));

					ticketsNumber++;

					passengerIds.push(traveller.id);
				}
			});

			if (ticketsNumber > 0) {
				servicesToRefund.push({
					serviceId: service.id,
					totalPrice: priceToRefund,
					type: service.category,
					serviceName: {
						name: service.name,
						terminal: service.terminalName
					},
					ticketsNumber,
					passengerNames,
					passengerIds
				});
			}
		});

		return servicesToRefund;
	}, [props.selectedServices, props.services, travellers]);

	React.useEffect(() => {
		if (refundedServiceIds?.length > 0 || refundedWithError?.length > 0) {
			props.onSuccessfullyRefunded();
		}
	}, [refundedServiceIds, refundedWithError]);

	const onRefundHandler = () => {
		if (activeCheckbox) {
			refund(servicesToRefund);
		} else {
			setShowAgreeTooltip(true);
		}
	};

	return (
		<div className={css.nasServicesRefund}>
			<div className={css.nasServices}>
				{servicesToRefund.map((service, key) => (
					<NasServiceCard
						type={service.type}
						serviceName={service.serviceName}
						totalPrice={service.totalPrice}
						passengerNames={service.passengerNames}
						key={key}
						ticketsNumber={service.ticketsNumber}
						travellers={travellers}
					/>
				))}
			</div>

			{!!refundedWithError?.length && (
				<Notification
					status={InfoMessageTypeEnum.Info}
					type="context"
					infoMessage={{
						title: t('Couldn\'t return the service(s).'),
						text: t('Please, try again later or contact the airline'),
						type: InfoMessageTypeEnum.Info,
						code: 'notRefunded'
					}}
				/>
			)}

			<div className={css.nasServicesActions}>
				<Tooltip title={t('Please, read the rules of service and accept it')} open={showAgreeTooltip}>
					<FormControlLabel
						className={css.agree}
						checked={activeCheckbox}
						onChange={(_, value) => setActiveCheckbox(value)}
						control={<Checkbox classes={{ root: css.checkbox, checked: css.active }} />}
						label={<InfoLinksLabel type={'refund'} order={props.order} />}
					/>
				</Tooltip>

				<div className={css.buttons}>
					<Button variant="smallSecondary" className={css.cancel} disabled={loading} onClick={props.onCancel}>
						{t('Reject')}
					</Button>
					<Button
						variant="action"
						className={css.confirm}
						isLoading={loading}
						disabled={!activeCheckbox}
						onClick={onRefundHandler}
					>
						{t(servicesToRefund.length === 1 ? 'Refund service' : 'Refund services')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default NasRefundServices;
